import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  Trash2,
  KeyRound,
  UserCircle,
  Mail,
  FileText,
  MessageSquare,
  ShieldCheck,
  Calendar,
} from "lucide-react";
import MainLayout from "../../../layout";
import { TextView } from "../../../components/view-form-components/text-view";
import ResetPasswordModal from "../../../components/modal/reset-password-modal";
import DeleteModal from "../../../components/modal/delete-modal";
import CommentsComponent from "../../../components/comments/comments";
import FileCommentComponent from "../../../components/comments/file-comment";
import Loader from "../../../assets/icons/loader/loader";
import { url } from "../../../config";

const ReadUsers = () => {
  const params = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState();
  const [deleteModalShow, setdeleteModalShow] = useState(false);
  const [openResetPassword, setOpenResetPassword] = useState(false);

  const fetchData = () => {
    setLoading(true);
    axios
      .post(`${url}/ffadmin/read`, { id: params?.id })
      .then((res) => {
        setData(res.data.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("API Error:", err);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchData();
  }, [params?.id]);

  const deleteFnc = (id) => {
    axios
      .post(`${url}/ffadmin/delete`, { id: id })
      .then(() => {
        navigate("/admin/users/list");
      })
      .catch((err) => console.error("API Error:", err));
  };

  if (loading)
    return (
      <MainLayout>
        <div className="h-full overflow-hidden">
          <header className="sticky top-0 z-20 bg-white/80 backdrop-blur-md border-b border-slate-200 px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/admin/users/list")}
                className="p-2 hover:bg-slate-100 rounded-full transition-colors text-slate-600"
              >
                <ArrowLeft size={20} />
              </button>
              <h1 className="text-lg font-bold text-slate-800">Admin User</h1>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setOpenResetPassword(true)}
                className="hidden md:flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-600 rounded-xl font-bold text-sm hover:bg-slate-50 transition-all shadow-sm"
              >
                <KeyRound size={16} /> Reset Password
              </button>
              <button
                onClick={() => setdeleteModalShow(true)}
                className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 border border-red-100 rounded-xl font-bold text-sm hover:bg-red-600 hover:text-white transition-all shadow-sm"
              >
                <Trash2 size={16} /> Delete
              </button>
            </div>
          </header>
          <div className="flex flex-col items-center justify-center bg-slate-50 h-[80vh] rounded-b-2xl">
            <div className="h-16 w-16">
              <Loader />
            </div>
            <p className="text-slate-400 font-medium mt-4 animate-pulse">
              Fetching account details...
            </p>
          </div>{" "}
        </div>
      </MainLayout>
    );

  return (
    <MainLayout>
      <DeleteModal
        isOpen={deleteModalShow}
        onClose={() => setdeleteModalShow(false)}
        btnFnc={() => deleteFnc(params?.id)}
      />
      <ResetPasswordModal
        id={params?.id}
        isOpen={openResetPassword}
        onClose={() => setOpenResetPassword(false)}
      />

      <div className="flex flex-col h-full bg-[#F8FAFC]">
        {/* --- Action Header --- */}
        <header className="sticky top-0 z-20 bg-white/80 backdrop-blur-md border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/admin/users/list")}
              className="p-2 hover:bg-slate-100 rounded-full transition-colors text-slate-600"
            >
              <ArrowLeft size={20} />
            </button>
            <h1 className="text-lg font-bold text-slate-800">Admin User</h1>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setOpenResetPassword(true)}
              className="hidden md:flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-600 rounded-xl font-bold text-sm hover:bg-slate-50 transition-all shadow-sm"
            >
              <KeyRound size={16} /> Reset Password
            </button>
            <button
              onClick={() => setdeleteModalShow(true)}
              className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 border border-red-100 rounded-xl font-bold text-sm hover:bg-red-600 hover:text-white transition-all shadow-sm"
            >
              <Trash2 size={16} /> Delete
            </button>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* --- Left Column: Summary Card --- */}
            <div className="lg:col-span-4 space-y-6">
              <div className="bg-white rounded-3xl border border-slate-200 overflow-hidden shadow-sm">
                <div className="h-24 bg-gradient-to-r from-blue-600 to-indigo-700" />
                <div className="px-6 pb-8 -mt-12 flex flex-col items-center text-center">
                  <div className="w-44 h-44 p-1 bg-white rounded-full shadow-lg mb-4">
                    <img
                      src={data?.profile_picture}
                      className="w-full h-full rounded-full object-cover"
                    />
                  </div>
                  <h2 className="text-xl font-black text-slate-800">
                    {data?.name}
                  </h2>
                  <p className="text-sm text-slate-500 font-medium flex items-center gap-1">
                    <Mail size={14} /> {data?.email}
                  </p>

                  <div className="mt-6 w-full pt-6 border-t border-slate-50 flex justify-around">
                    <div className="text-center">
                      <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">
                        Status
                      </p>
                      <span className="inline-flex items-center gap-1.5 py-1 px-3 mt-1 rounded-full bg-emerald-50 text-emerald-600 text-xs font-bold border border-emerald-100">
                        <ShieldCheck size={12} /> Active
                      </span>
                    </div>
                    <div className="text-center">
                      <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">
                        Role
                      </p>
                      <p className="text-xs font-bold text-slate-700 mt-1.5 uppercase">
                        Administrator
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action shortcuts for Mobile */}
              <div className="md:hidden space-y-2">
                <button
                  onClick={() => setOpenResetPassword(true)}
                  className="w-full py-3 bg-white border border-slate-200 rounded-xl text-sm font-bold"
                >
                  Reset Password
                </button>
              </div>
            </div>

            {/* --- Right Column: Details & Communication --- */}
            <div className="lg:col-span-8 space-y-6">
              {/* Basic Details Grid */}
              <div className="bg-white rounded-3xl border border-slate-200 p-8 shadow-sm">
                <div className="flex items-center gap-2 mb-6 pb-4 border-b border-slate-50">
                  <UserCircle size={18} className="text-blue-600" />
                  <h2 className="font-bold text-slate-800 uppercase text-xs tracking-widest">
                    Account Details
                  </h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-y-8 gap-x-12">
                  <TextView label="Full Name" value={data?.name} />
                  <TextView label="Email Address" value={data?.email} />
                  <div className="md:col-span-2">
                    <TextView
                      label="Bio / Description"
                      value={data?.description || "No description provided."}
                    />
                  </div>
                </div>
              </div>

              {/* Tabs / Multi-section for Comments and Files */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white rounded-3xl border border-slate-200 p-6 shadow-sm">
                  <div className="flex items-center gap-2 mb-6 border-b border-slate-50 pb-4">
                    <MessageSquare size={18} className="text-primary" />
                    <h3 className="font-bold text-slate-800 text-sm tracking-wide">
                      Recent Activity
                    </h3>
                  </div>
                  <CommentsComponent />
                </div>

                <div className="bg-white rounded-3xl border border-slate-200 p-6 shadow-sm">
                  <div className="flex items-center gap-2 mb-6 border-b border-slate-50 pb-4">
                    {/* <FileText size={18} className="text-amber-600" /> */}
                    <h3 className="font-bold text-slate-800 text-sm tracking-wide">
                      Attachments
                    </h3>
                  </div>
                  <FileCommentComponent />
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </MainLayout>
  );
};

export default ReadUsers;
